import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  signInAnonymously,
  updateProfile
} from 'firebase/auth';
import { ref, set, get, child } from 'firebase/database';
import { auth, db } from '../config/firebase';

export interface UserProfile {
  uid: string;
  email?: string;
  displayName?: string;
  role: 'researcher' | 'admin' | 'anonymous';
  createdAt: string;
  lastLogin: string;
  isAnonymous: boolean;
}

export class FirebaseAuthService {
  
  // Register new user with email/password
  static async registerUser(email: string, password: string, displayName: string): Promise<UserProfile> {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update display name
      await updateProfile(user, { displayName });
      
      // Create user profile in database
      const userProfile: UserProfile = {
        uid: user.uid,
        email: user.email!,
        displayName,
        role: 'researcher',
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
        isAnonymous: false
      };
      
      // Save to database
      await set(ref(db, `users/${user.uid}`), userProfile);
      
      console.log('User registered successfully:', userProfile);
      return userProfile;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }
  
  // Login with email/password
  static async loginUser(email: string, password: string): Promise<UserProfile> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Get user profile from database
      const userRef = ref(db, `users/${user.uid}`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        const userProfile = snapshot.val() as UserProfile;
        
        // Update last login
        userProfile.lastLogin = new Date().toISOString();
        await set(userRef, userProfile);
        
        console.log('User logged in successfully:', userProfile);
        return userProfile;
      } else {
        // Create profile if doesn't exist (for existing users)
        const userProfile: UserProfile = {
          uid: user.uid,
          email: user.email!,
          displayName: user.displayName || email.split('@')[0],
          role: 'researcher',
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
          isAnonymous: false
        };
        
        await set(userRef, userProfile);
        return userProfile;
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }
  
  // Anonymous login for patients (no registration required)
  static async loginAnonymously(patientName?: string): Promise<UserProfile> {
    try {
      const userCredential = await signInAnonymously(auth);
      const user = userCredential.user;
      
      // Create anonymous user profile
      const userProfile: UserProfile = {
        uid: user.uid,
        displayName: patientName || `Patient_${Date.now()}`,
        role: 'anonymous',
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
        isAnonymous: true
      };
      
      // Save to database
      await set(ref(db, `users/${user.uid}`), userProfile);
      
      console.log('Anonymous user created:', userProfile);
      return userProfile;
    } catch (error) {
      console.error('Anonymous login error:', error);
      throw error;
    }
  }
  
  // Logout
  static async logout(): Promise<void> {
    try {
      await signOut(auth);
      console.log('User logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }
  
  // Get current user profile
  static async getCurrentUserProfile(): Promise<UserProfile | null> {
    const user = auth.currentUser;
    if (!user) return null;
    
    try {
      const userRef = ref(db, `users/${user.uid}`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        return snapshot.val() as UserProfile;
      }
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  }
  
  // Listen to auth state changes
  static onAuthStateChange(callback: (user: User | null) => void) {
    return onAuthStateChanged(auth, callback);
  }
  
  // Get current Firebase user
  static getCurrentUser(): User | null {
    return auth.currentUser;
  }
  
  // Check if user is logged in
  static isLoggedIn(): boolean {
    return !!auth.currentUser;
  }
  
  // Check if user is anonymous
  static isAnonymous(): boolean {
    return auth.currentUser?.isAnonymous || false;
  }
}